class Collection {
  constructor() {
    this.selectors = {
      section: ".facest-filters-section",
      toolbar: ".m-collection-toolbar",
      cols: [".m-toolbar--column-switcher button"],
      productContainer: "[data-product-container]",
      loadMoreBtn: "[data-load-more]",
      paginationWrap: ".m-collection--pagination",
      sidebar: ".m-sidebar",
      sidebarContent: ".m-sidebar--content",
      openSidebar: ".m-sidebar--open",
      closeSidebar: ".m-sidebar--close",
      filtersWrapper: ".m-filter--wrapper",
    };
    this.mobileSortingSelectors = {
      openSorting: ".m-sortby--open",
      closeSorting: ".m-sortby-mobile--close",
      sortingWrapper: ".m-sortby-mobile--wrapper",
      sortingList: ".m-sortby-mobile--content",
    };
    this.sectionId = "";
    this.isDesignMode = MinimogSettings.design_mode;

    this.currentPage = 1;
    this.paginationType;
    this.totalPages;
    this.nextUrl = "";
    this.isLoading = false;
    this.loadMorePaused = false;
    this.loadMoreRequestId = 0;
    this.infiniteLoadingObserver = null;
    this._boundLoadMore = null;

    this.showColSwitchers;
    this.initialCol;
    this.manualCol = null;
    this.activeCol = null;
    this.MAX_COL_BY_SCREEN_SIZE = {
      mobile: 2,
      tablet: 4,
      desktop: 5,
    };

    this.enableFilters;
    this.enableSorting;
    this.filtersType;
    this.filtersPosition;
    this.accordions;
    this.sideEffectEventsAdded = false;
    this._resizeHandler = null;
    this._stickyScrollHandler = null;
  }

  init = () => {
    this.destroyLoadMore();
    this.domNodes = queryDomNodes(this.selectors);
    this.mbSortingNodes = queryDomNodes(this.mobileSortingSelectors);
    this.setData();
    this.initGridView();
    this.initFilters();
    this.initMobileSorting();
    this.initProducts();
    this.sideEffectEventsAdded = true;
    if (!this._productListUpdatedSubscribed) {
      MinimogEvents.subscribe("ON_PRODUCT_LIST_UPDATED", () => refreshProductReview());
      this._productListUpdatedSubscribed = true;
    }

    this.handleStickyToolbar();
    if (!this._mobileMatchBound) {
      document.addEventListener("matchMobile", () => this.handleStickyToolbar());
      document.addEventListener("unmatchMobile", () => this.handleStickyToolbar());
      this._mobileMatchBound = true;
    }
  };

  destroy = () => {
    this.abortPendingLoadMore();
    this.destroyLoadMore();
    this.hideLoadMoreError();
    if (this.accordions && typeof this.accordions.destroy === "function") {
      this.accordions.destroy();
      this.accordions = null;
    }
  };

  setData = () => {
    const { section, productContainer } = this.domNodes;
    this.sectionId = section.dataset.sectionId;
    this.paginationType = section.dataset.paginationType;
    this.view = section.dataset.view;
    this.initialCol = Number(section.dataset.initialColumn) || 4;
    this.showColSwitchers = section.dataset.showColSwitchers === "true";
    this.enableFilters = section.dataset.enableFilters === "true";
    this.enableSorting = section.dataset.enableSorting === "true";
    this.filtersType = section.dataset.filtersType;
    this.filtersPosition = section.dataset.filtersPosition;

    const urlPage = Number(new URLSearchParams(window.location.search).get("page")) || 0;
    const dataPage = productContainer && Number(productContainer.dataset.page);
    this.currentPage = dataPage || urlPage || 1;
    this.totalPages = productContainer && Number(productContainer.dataset.totalPages);
    this.nextUrl = (productContainer && productContainer.dataset.nextUrl) || "";

    // Preserve manual column choice across AJAX re-renders of the same collection session
    if (this.manualCol == null && productContainer) {
      const match = [...productContainer.classList].find((c) => /^m-cols-\d+$/.test(c));
      if (match) {
        const fromDom = Number(match.replace("m-cols-", ""));
        if (fromDom && fromDom !== this.initialCol) this.manualCol = fromDom;
      }
    }
  };

  initGridView = () => {
    const startCol = this.manualCol || this.initialCol;
    this.applyColumnView(startCol, { fromManual: false });
    this.updateViewByScreen();

    if (!this._colsDelegateBound) {
      addEventDelegate({
        selector: this.selectors.cols[0],
        handler: (e, colNode) => {
          if (!colNode.closest(".facest-filters-section")) return;
          const col = Number(colNode.dataset.column);
          this.manualCol = col;
          this.applyColumnView(col, { fromManual: true });
        },
      });
      this._colsDelegateBound = true;
    }

    this.initLoadMore();

    if (!this.sideEffectEventsAdded) {
      this._resizeHandler = debounce(this.updateViewByScreen, 500);
      window.addEventListener("resize", this._resizeHandler);
    }
  };

  initFilters = () => {
    if (!this.enableFilters) return;
    const { sidebar, openSidebar, closeSidebar } = this.domNodes;
    if (!sidebar || !openSidebar || !closeSidebar) return;
    window.requestAnimationFrame(this.initAccordions);
    openSidebar.addEventListener("click", this.openSidebarFilter);
    closeSidebar.addEventListener("click", this.closeSidebarFilter);
    sidebar.addEventListener("click", (e) => e.target === sidebar && this.closeSidebarFilter());
  };

  initMobileSorting = () => {
    if (!this.enableSorting) return;
    const { openSorting, closeSorting, sortingWrapper } = this.mbSortingNodes;
    if (!openSorting || !closeSorting || !sortingWrapper) return;
    openSorting.addEventListener("click", this.openMobileSorting);
    closeSorting.addEventListener("click", this.closeMobileSorting);
    sortingWrapper.addEventListener("click", (e) => e.target === sortingWrapper && this.closeMobileSorting());
  };

  openMobileSorting = () => {
    const { sortingWrapper, sortingList } = this.mbSortingNodes;
    sortingWrapper.style.display = "block";
    window.requestAnimationFrame(() => {
      sortingWrapper.style.setProperty("--m-bg-opacity", "0.5");
      sortingList.style.setProperty("--m-translate-y", "0");
    });
  };

  closeMobileSorting = () => {
    const { sortingWrapper, sortingList } = this.mbSortingNodes;
    sortingWrapper.style.setProperty("--m-bg-opacity", "0");
    sortingList.style.setProperty("--m-translate-y", "100%");
    setTimeout(() => sortingWrapper.style.setProperty("display", "none"), 300);
  };

  initAccordions = () => {
    this.accordions && this.accordions.destroy();
    const { filtersWrapper } = this.domNodes;
    if (!filtersWrapper) return;
    filtersWrapper.classList.remove("acc-initialized");
    this.accordions = new MinimogLibs.Accordion(filtersWrapper, {
      presetContentHeight: window.innerWidth > 1280 && this.filtersPosition !== "fixed",
      callback: () => filtersWrapper.style.setProperty("opacity", "1"),
    });
  };

  openSidebarFilter = () => {
    const { sidebar, sidebarContent, section } = this.domNodes;
    sidebar.style.display = "block";
    window.requestAnimationFrame(() => {
      sidebar.style.setProperty("--m-bg-opacity", "0.5");
      sidebarContent.style.setProperty("--m-translate-x", "0");
      this.accordions && this.accordions.setContentHeight();
    });
    section.classList.add("sidebar-open");
  };

  closeSidebarFilter = () => {
    const { sidebar, sidebarContent, section } = this.domNodes;
    section.classList.remove("sidebar-open");
    if (window.innerWidth < 1280 || sidebar.dataset.type === "fixed") {
      sidebarContent.style.setProperty("--m-translate-x", "-100%");
      sidebar.style.removeProperty("--m-bg-opacity");
      setTimeout(() => sidebar.style.removeProperty("display"), 300);
    }
  };

  applyColumnView = (col, { fromManual = false } = {}) => {
    if (!col || this.view === "search") return;
    const { productContainer } = this.domNodes;
    if (!productContainer) return;

    const allImages = productContainer.querySelectorAll("responsive-image");
    if (allImages && allImages.length > 0) {
      allImages.forEach((img) => {
        img.disconnectedCallback();
        img.connectedCallback();
      });
    }

    const prevCol = this.activeCol || this.initialCol;
    if (prevCol) productContainer.classList.remove(`m-cols-${prevCol}`);
    productContainer.classList.add(`m-cols-${col}`);
    this.activeCol = col;

    this.updateColumnButtons(col);

    // Do not persist to localStorage — preference lasts only for this page session / AJAX updates
    if (fromManual) this.manualCol = col;
  };

  updateColumnButtons = (col) => {
    if (!this.showColSwitchers || !this.domNodes.toolbar) return;
    const buttons = this.domNodes.toolbar.querySelectorAll(".m-toolbar--column-switcher button[data-column]");
    buttons.forEach((btn) => {
      const isActive = Number(btn.dataset.column) === Number(col);
      btn.classList.toggle("active", isActive);
      btn.setAttribute("aria-pressed", isActive ? "true" : "false");
    });
  };

  getColForScreen = (preferred = this.manualCol || this.initialCol) => {
    if (preferred === 1) return 1; // list
    const maxCol = this.MAX_COL_BY_SCREEN_SIZE[this.getDeviceByScreenSize()];
    return Math.min(preferred, maxCol);
  };

  updateViewByScreen = () => {
    if (this.activeCol === 1 || this.manualCol === 1) return; // List layout
    const target = this.getColForScreen(this.manualCol || this.initialCol);
    if (target !== this.activeCol) {
      this.applyColumnView(target, { fromManual: false });
    }
  };

  getDeviceByScreenSize = () => {
    if (window.innerWidth < 768) return "mobile";
    if (window.innerWidth < 1024) return "tablet";
    return "desktop";
  };

  destroyLoadMore = () => {
    const btn = this.domNodes?.loadMoreBtn;
    if (btn && this._boundLoadMore) {
      btn.removeEventListener("click", this._boundLoadMore);
    }
    if (this.infiniteLoadingObserver) {
      this.infiniteLoadingObserver.disconnect();
      this.infiniteLoadingObserver = null;
    }
    this._boundLoadMore = null;
  };

  abortPendingLoadMore = () => {
    this.loadMoreRequestId += 1;
    this.isLoading = false;
    this.loadMorePaused = false;
    this.toggleLoading(false);
  };

  initLoadMore = () => {
    this.destroyLoadMore();
    if (this.paginationType === "paginate" || this.totalPages <= 1 || this.view === "search") return;

    const { loadMoreBtn } = this.domNodes;
    if (!loadMoreBtn) return;

    this._boundLoadMore = (e) => {
      e.preventDefault();
      this.loadMorePaused = false;
      this.hideLoadMoreError();
      this.loadMoreProducts();
    };
    loadMoreBtn.addEventListener("click", this._boundLoadMore);

    if (this.paginationType === "infinite") {
      this.infiniteLoadingObserver = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          if (!entry?.isIntersecting || this.loadMorePaused || this.isLoading) return;
          this.loadMoreProducts();
        },
        { rootMargin: "200px 0px", threshold: 0 }
      );
      this.infiniteLoadingObserver.observe(loadMoreBtn);
    }
  };

  loadMoreProducts = () => {
    if (this.isLoading || this.loadMorePaused) return Promise.resolve();
    if (this.currentPage >= this.totalPages) return Promise.resolve();

    const nextPage = this.currentPage + 1;
    const { productContainer, loadMoreBtn } = this.domNodes;
    if (!productContainer) return Promise.resolve();

    const requestId = ++this.loadMoreRequestId;
    this.isLoading = true;
    this.toggleLoading(true);
    this.hideLoadMoreError();

    const params = { page: nextPage };
    // Prefer Shopify paginate.next URL query (keeps filters/sort) when available
    if (this.nextUrl) {
      try {
        const next = new URL(this.nextUrl, window.location.origin);
        next.searchParams.forEach((v, k) => {
          if (k !== "section_id") params[k] = v;
        });
        params.page = nextPage;
      } catch (e) {
        // fall back to page param only
      }
    }

    return fetchSection(this.sectionId, { fromCache: false, params })
      .then((productGridHTML) => {
        if (requestId !== this.loadMoreRequestId) return; // stale

        const productNodes = productGridHTML.querySelectorAll("[data-product-container] .m-product-item");
        if (!productNodes.length && nextPage < this.totalPages) {
          throw new Error("Empty page response");
        }

        productNodes.forEach((prodNode) => productContainer.appendChild(prodNode));

        const fetchedContainer = productGridHTML.querySelector("[data-product-container]");
        if (fetchedContainer) {
          this.totalPages = Number(fetchedContainer.dataset.totalPages) || this.totalPages;
          this.nextUrl = fetchedContainer.dataset.nextUrl || "";
        }

        this.currentPage = nextPage;
        productContainer.dataset.page = String(this.currentPage);
        this.initProducts();
        MinimogEvents.emit("ON_PRODUCT_LIST_UPDATED");

        if (this.currentPage >= this.totalPages) {
          const wrap = loadMoreBtn?.parentNode;
          this.destroyLoadMore();
          if (wrap) wrap.remove();
        } else if (this.paginationType === "infinite" && !this.loadMorePaused) {
          requestAnimationFrame(() => {
            const btn = this.domNodes.loadMoreBtn;
            if (!btn || this.isLoading) return;
            const rect = btn.getBoundingClientRect();
            if (rect.top < window.innerHeight + 200) this.loadMoreProducts();
          });
        }
      })
      .catch((err) => {
        if (requestId !== this.loadMoreRequestId) return;
        console.error("Failed to load more products.", err);
        this.loadMorePaused = true;
        this.showLoadMoreError();
      })
      .finally(() => {
        if (requestId !== this.loadMoreRequestId) return;
        this.isLoading = false;
        this.toggleLoading(false);
      });
  };

  showLoadMoreError = () => {
    const wrap = this.domNodes.paginationWrap || this.domNodes.loadMoreBtn?.parentNode;
    if (!wrap) return;
    let errorEl = wrap.querySelector("[data-load-more-error]");
    if (!errorEl) {
      errorEl = document.createElement("div");
      errorEl.setAttribute("data-load-more-error");
      errorEl.setAttribute("role", "alert");
      errorEl.className = "m-collection--load-more-error m:mt-4";
      errorEl.innerHTML = `<p class="m:mb-2">${
        (window.MinimogStrings && MinimogStrings.cartError) || "Something went wrong. Please try again."
      }</p><button type="button" class="m-button m-button--secondary" data-load-more-retry>Retry</button>`;
      wrap.appendChild(errorEl);
      errorEl.querySelector("[data-load-more-retry]")?.addEventListener("click", () => {
        this.loadMorePaused = false;
        this.hideLoadMoreError();
        this.loadMoreProducts();
      });
    }
    errorEl.hidden = false;
  };

  hideLoadMoreError = () => {
    const wrap = this.domNodes?.paginationWrap || this.domNodes?.loadMoreBtn?.parentNode;
    const errorEl = wrap?.querySelector?.("[data-load-more-error]");
    if (errorEl) errorEl.hidden = true;
  };

  initProducts = () => {
    MinimogTheme.CompareProduct && MinimogTheme.CompareProduct.setCompareButtonsState();
    MinimogTheme.Wishlist && MinimogTheme.Wishlist.setWishlistButtonsState();
  };

  toggleLoading = (loading) => {
    const { loadMoreBtn } = this.domNodes || {};
    if (loadMoreBtn) {
      loadMoreBtn.classList[loading ? "add" : "remove"]("m-spinner-loading");
      loadMoreBtn.setAttribute("aria-busy", loading ? "true" : "false");
    }
  };

  handleStickyToolbar() {
    const toolbar = this.domNodes.toolbar;
    if (!toolbar) return;
    if (this._stickyScrollHandler) {
      window.removeEventListener("scroll", this._stickyScrollHandler);
    }
    const extraSpace = toolbar.offsetTop;
    let lastScroll = 0;
    this._stickyScrollHandler = () => {
      if (MinimogTheme.config.mqlMobile) {
        const currentScroll = window.scrollY;
        if (currentScroll <= extraSpace) {
          toolbar.classList.remove("scroll-up", "m-collection-toolbar--sticky");
          return;
        }
        toolbar.classList.add("m-collection-toolbar--sticky");
        if (
          currentScroll > toolbar.offsetHeight + extraSpace &&
          currentScroll > lastScroll &&
          !toolbar.classList.contains("scroll-down")
        ) {
          toolbar.classList.remove("scroll-up");
          toolbar.classList.add("scroll-down");
        } else if (currentScroll < lastScroll && toolbar.classList.contains("scroll-down")) {
          toolbar.classList.remove("scroll-down");
          toolbar.classList.add("scroll-up");
        }
        lastScroll = currentScroll;
      } else {
        toolbar.classList.remove("scroll-up", "m-collection-toolbar--sticky", "scroll-down");
      }
    };
    window.addEventListener("scroll", this._stickyScrollHandler);
  }
}
